import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// API base URL
const API_BASE_URL = "";

export default function BillsTransactionsDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    successful: 0,
    pending: 0,
    failed: 0,
    reversed: 0,
    totalAmount: 0,
    successfulAmount: 0,
    pendingAmount: 0,
    failedAmount: 0,
    reversedAmount: 0
  });
  const [filters, setFilters] = useState({
    status: "all",
    type: "all",
    dateRange: "today",
    search: "",
    page: 1,
    pageSize: 20
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refreshInterval] = useState(30);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-NG", {
      dateStyle: "medium",
      timeStyle: "short"
    });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    };
  };

  const fetchTransactions = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        page: filters.page,
        pageSize: filters.pageSize,
        ...(filters.status !== "all" && { status: filters.status }),
        ...(filters.type !== "all" && { type: filters.type }),
        ...(filters.dateRange !== "all" && { dateRange: filters.dateRange }),
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/vtu/transactions?${queryParams}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error("Failed to fetch transactions");

      const data = await response.json();
      setTransactions(data.data || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters.dateRange !== "all" && { dateRange: filters.dateRange })
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/vtu/stats?${queryParams}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error("Failed to fetch stats");

      const data = await response.json();
      if (data.data) {
        setStats(data.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStats({
        total: 0,
        successful: 0,
        pending: 0,
        failed: 0,
        reversed: 0,
        totalAmount: 0,
        successfulAmount: 0,
        pendingAmount: 0,
        failedAmount: 0,
        reversedAmount: 0
      });
    } finally {
      setLoading(false);
    }
  }, [filters.dateRange]);

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [fetchTransactions, fetchStats]);

  useEffect(() => {
    if (!isAutoRefresh) return;
    
    const interval = setInterval(() => {
      fetchTransactions();
      fetchStats();
    }, refreshInterval * 1000);
    
    return () => clearInterval(interval);
  }, [isAutoRefresh, refreshInterval, fetchTransactions, fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchTransactions();
    fetchStats();
  };

  const handleExport = () => {
    const csvContent = [
      ["ID", "Request ID", "Type", "Provider", "Beneficiary", "Amount", "Status", "User", "Date"].join(","),
      ...transactions.map(t => [
        t.id,
        t.requestId,
        t.type,
        t.provider,
        t.beneficiary,
        t.amount,
        t.status,
        t.userName,
        formatDate(t.createdAt)
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bills-transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": case "success": return "#10b981";
      case "processing": case "pending": return "#f59e0b";
      case "failed": return "#ef4444";
      case "reversed": case "refunded": return "#8b5cf6";
      default: return "#6b7280";
    }
  };

  const getStatusBg = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": case "success": return "#d1fae5";
      case "processing": case "pending": return "#fef3c7";
      case "failed": return "#fee2e2";
      case "reversed": case "refunded": return "#ede9fe";
      default: return "#f3f4f6";
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "airtime": return "📱";
      case "data": return "📶";
      case "electricity": return "⚡";
      case "cable tv": case "cabletv": return "📺";
      default: return "💳";
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading transactions...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,0,0,0.15); }
        .tx-row:hover { background: #f8fafc; }
        .filter-select:focus { border-color: #1183FB; outline: none; }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate("/admin")} style={styles.backBtn}>←</button>
          <div>
            <h1 style={styles.headerTitle}>Bills Transactions</h1>
            <p style={styles.headerSubtitle}>Real-time monitoring dashboard</p>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.refreshInfo}>
            <span style={styles.lastRefreshText}>Last updated: {lastRefresh.toLocaleTimeString()}</span>
            <label style={styles.autoRefreshLabel}>
              <input 
                type="checkbox" 
                checked={isAutoRefresh} 
                onChange={(e) => setIsAutoRefresh(e.target.checked)}
                style={styles.checkbox}
              />
              Auto-refresh ({refreshInterval}s)
            </label>
          </div>
          <button onClick={handleRefresh} style={styles.refreshBtn}>🔄 Refresh</button>
          <button onClick={handleExport} style={styles.exportBtn}>📥 Export CSV</button>
        </div>
      </header>

      <main style={styles.main}>
        {/* Stats Cards */}
        <div style={styles.statsGrid}>
          <div className="stat-card" style={{...styles.statCard, borderLeft: "4px solid #1183FB"}}>
            <div style={styles.statIcon}>📊</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>Total Transactions</p>
              <h3 style={styles.statValue}>{stats.total?.toLocaleString() || 0}</h3>
              <p style={styles.statAmount}>{formatCurrency(stats.totalAmount)}</p>
            </div>
          </div>
          
          <div className="stat-card" style={{...styles.statCard, borderLeft: "4px solid #10b981"}} onClick={() => handleFilterChange("status", "completed")}>
            <div style={{...styles.statIcon, background: "#d1fae5"}}>✅</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>Successful</p>
              <h3 style={{...styles.statValue, color: "#10b981"}}>{stats.successful?.toLocaleString() || 0}</h3>
              <p style={styles.statAmount}>{formatCurrency(stats.successfulAmount)}</p>
            </div>
          </div>
          
          <div className="stat-card" style={{...styles.statCard, borderLeft: "4px solid #f59e0b"}} onClick={() => handleFilterChange("status", "pending")}>
            <div style={{...styles.statIcon, background: "#fef3c7"}}>⏳</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>Pending</p>
              <h3 style={{...styles.statValue, color: "#f59e0b"}}>{stats.pending?.toLocaleString() || 0}</h3>
              <p style={styles.statAmount}>{formatCurrency(stats.pendingAmount)}</p>
            </div>
          </div>
          
          <div className="stat-card" style={{...styles.statCard, borderLeft: "4px solid #ef4444"}} onClick={() => handleFilterChange("status", "failed")}>
            <div style={{...styles.statIcon, background: "#fee2e2"}}>❌</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>Failed</p>
              <h3 style={{...styles.statValue, color: "#ef4444"}}>{stats.failed?.toLocaleString() || 0}</h3>
              <p style={styles.statAmount}>{formatCurrency(stats.failedAmount)}</p>
            </div>
          </div>
          
          <div className="stat-card" style={{...styles.statCard, borderLeft: "4px solid #8b5cf6"}} onClick={() => handleFilterChange("status", "reversed")}>
            <div style={{...styles.statIcon, background: "#ede9fe"}}>🔄</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>Reversed</p>
              <h3 style={{...styles.statValue, color: "#8b5cf6"}}>{stats.reversed?.toLocaleString() || 0}</h3>
              <p style={styles.statAmount}>{formatCurrency(stats.reversedAmount)}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filtersBar}>
          <div style={styles.filterGroup}>
            <input
              type="text"
              placeholder="🔍 Search by ID, phone, email..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              style={styles.searchInput}
              className="filter-select"
            />
          </div>
          
          <div style={styles.filterGroup}>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              style={styles.filterSelect}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="completed">✅ Successful</option>
              <option value="pending">⏳ Pending</option>
              <option value="failed">❌ Failed</option>
              <option value="reversed">🔄 Reversed</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              style={styles.filterSelect}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="airtime">📱 Airtime</option>
              <option value="data">📶 Data</option>
              <option value="electricity">⚡ Electricity</option>
              <option value="cable tv">📺 Cable TV</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange("dateRange", e.target.value)}
              style={styles.filterSelect}
              className="filter-select"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
          
          {filters.status !== "all" || filters.type !== "all" || filters.search ? (
            <button 
              onClick={() => setFilters({...filters, status: "all", type: "all", search: ""})}
              style={styles.clearFiltersBtn}
            >
              ✕ Clear Filters
            </button>
          ) : null}
        </div>

        {/* Transactions Table */}
        <div style={styles.tableContainer}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>
              Transactions ({transactions.length})
              {isAutoRefresh && <span style={styles.liveIndicator}>● LIVE</span>}
            </h3>
          </div>
          
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeadRow}>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Request ID</th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Provider</th>
                  <th style={styles.th}>Beneficiary</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={styles.emptyState}>
                      <div style={styles.emptyIcon}>📭</div>
                      <p>No transactions found</p>
                      <p style={{fontSize: "13px", color: "#94a3b8"}}>Try adjusting your filters or date range</p>
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.id} className="tx-row" style={styles.tableRow}>
                      <td style={styles.td}>
                        <span style={styles.typeCell}>
                          <span style={styles.typeIcon}>{getTypeIcon(tx.type)}</span>
                          {tx.type}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.requestId}>{tx.requestId?.slice(-15) || "N/A"}</span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.userCell}>
                          <span style={styles.userName}>{tx.userName || "N/A"}</span>
                          <span style={styles.userEmail}>{tx.userEmail || ""}</span>
                        </div>
                      </td>
                      <td style={styles.td}>{tx.provider || "N/A"}</td>
                      <td style={styles.td}>
                        <span style={styles.beneficiary}>{tx.beneficiary || "N/A"}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.amount}>{formatCurrency(tx.amount)}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge,
                          background: getStatusBg(tx.status),
                          color: getStatusColor(tx.status)
                        }}>
                          {tx.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.dateCell}>{formatDate(tx.createdAt)}</span>
                      </td>
                      <td style={styles.td}>
                        <button 
                          onClick={() => setSelectedTransaction(tx)}
                          style={styles.viewBtn}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div style={styles.pagination}>
            <button 
              onClick={() => handleFilterChange("page", Math.max(1, filters.page - 1))}
              disabled={filters.page === 1}
              style={{...styles.pageBtn, opacity: filters.page === 1 ? 0.5 : 1}}
            >
              ← Previous
            </button>
            <span style={styles.pageInfo}>Page {filters.page}</span>
            <button 
              onClick={() => handleFilterChange("page", filters.page + 1)}
              disabled={transactions.length < filters.pageSize}
              style={{...styles.pageBtn, opacity: transactions.length < filters.pageSize ? 0.5 : 1}}
            >
              Next →
            </button>
          </div>
        </div>
      </main>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div style={styles.modalOverlay} onClick={() => setSelectedTransaction(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Transaction Details</h3>
              <button onClick={() => setSelectedTransaction(null)} style={styles.modalClose}>✕</button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.detailSection}>
                <div style={styles.detailHeader}>
                  <span style={styles.detailIcon}>{getTypeIcon(selectedTransaction.type)}</span>
                  <div>
                    <h4 style={styles.detailType}>{selectedTransaction.type}</h4>
                    <span style={{
                      ...styles.statusBadge,
                      background: getStatusBg(selectedTransaction.status),
                      color: getStatusColor(selectedTransaction.status)
                    }}>
                      {selectedTransaction.status}
                    </span>
                  </div>
                  <span style={styles.detailAmount}>{formatCurrency(selectedTransaction.amount)}</span>
                </div>
              </div>
              
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Request ID</span>
                  <span style={styles.detailValue}>{selectedTransaction.requestId || "N/A"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Provider</span>
                  <span style={styles.detailValue}>{selectedTransaction.provider || "N/A"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Beneficiary</span>
                  <span style={styles.detailValue}>{selectedTransaction.beneficiary || "N/A"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>User</span>
                  <span style={styles.detailValue}>{selectedTransaction.userName || "N/A"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email</span>
                  <span style={styles.detailValue}>{selectedTransaction.userEmail || "N/A"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Created At</span>
                  <span style={styles.detailValue}>{formatDate(selectedTransaction.createdAt)}</span>
                </div>
                {selectedTransaction.completedAt && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Completed At</span>
                    <span style={styles.detailValue}>{formatDate(selectedTransaction.completedAt)}</span>
                  </div>
                )}
                {selectedTransaction.token && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Token</span>
                    <span style={{...styles.detailValue, fontFamily: "monospace", fontSize: "14px"}}>{selectedTransaction.token}</span>
                  </div>
                )}
                {selectedTransaction.planName && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Plan</span>
                    <span style={styles.detailValue}>{selectedTransaction.planName}</span>
                  </div>
                )}
                {selectedTransaction.errorMessage && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Error</span>
                    <span style={{...styles.detailValue, color: "#ef4444"}}>{selectedTransaction.errorMessage}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div style={styles.modalFooter}>
              <button onClick={() => setSelectedTransaction(null)} style={styles.closeBtn}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" },
  loadingContainer: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: "16px" },
  spinner: { width: "48px", height: "48px", border: "4px solid #e2e8f0", borderTopColor: "#1183FB", borderRadius: "50%", animation: "spin 1s linear infinite" },
  
  header: { background: "linear-gradient(135deg, #011745, #1183FB)", padding: "20px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },
  backBtn: { background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "12px", color: "white", width: "44px", height: "44px", fontSize: "20px", cursor: "pointer" },
  headerTitle: { color: "white", fontSize: "24px", fontWeight: "700", margin: 0 },
  headerSubtitle: { color: "rgba(255,255,255,0.8)", fontSize: "14px", margin: "4px 0 0 0" },
  headerRight: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  refreshInfo: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" },
  lastRefreshText: { color: "rgba(255,255,255,0.7)", fontSize: "12px" },
  autoRefreshLabel: { color: "white", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" },
  checkbox: { cursor: "pointer" },
  refreshBtn: { background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "10px", color: "white", padding: "10px 16px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  exportBtn: { background: "white", border: "none", borderRadius: "10px", color: "#011745", padding: "10px 16px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  
  main: { padding: "24px 32px", maxWidth: "1600px", margin: "0 auto" },
  
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", marginBottom: "24px" },
  statCard: { background: "white", borderRadius: "16px", padding: "20px", display: "flex", alignItems: "center", gap: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", cursor: "pointer", transition: "all 0.3s" },
  statIcon: { width: "50px", height: "50px", borderRadius: "12px", background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px" },
  statContent: { flex: 1 },
  statLabel: { color: "#64748b", fontSize: "13px", margin: 0, fontWeight: "500" },
  statValue: { color: "#011745", fontSize: "28px", fontWeight: "700", margin: "4px 0" },
  statAmount: { color: "#64748b", fontSize: "13px", margin: 0 },
  
  filtersBar: { background: "white", borderRadius: "16px", padding: "16px 20px", marginBottom: "24px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" },
  filterGroup: { display: "flex", alignItems: "center", gap: "8px" },
  searchInput: { padding: "10px 16px", border: "2px solid #e2e8f0", borderRadius: "10px", fontSize: "14px", width: "280px", outline: "none", transition: "border-color 0.2s" },
  filterSelect: { padding: "10px 16px", border: "2px solid #e2e8f0", borderRadius: "10px", fontSize: "14px", background: "white", cursor: "pointer", outline: "none", transition: "border-color 0.2s" },
  clearFiltersBtn: { background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: "8px", padding: "8px 12px", fontSize: "13px", fontWeight: "600", cursor: "pointer" },
  
  tableContainer: { background: "white", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden" },
  tableHeader: { padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" },
  tableTitle: { margin: 0, fontSize: "18px", fontWeight: "700", color: "#011745", display: "flex", alignItems: "center", gap: "12px" },
  liveIndicator: { color: "#10b981", fontSize: "12px", fontWeight: "600", animation: "pulse 2s infinite" },
  tableWrapper: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  tableHeadRow: { background: "#f8fafc" },
  th: { padding: "14px 16px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e2e8f0" },
  tableRow: { borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" },
  td: { padding: "14px 16px", fontSize: "14px", color: "#374151" },
  typeCell: { display: "flex", alignItems: "center", gap: "8px" },
  typeIcon: { fontSize: "18px" },
  requestId: { fontFamily: "monospace", fontSize: "12px", color: "#64748b" },
  userCell: { display: "flex", flexDirection: "column", gap: "2px" },
  userName: { fontWeight: "600", color: "#011745" },
  userEmail: { fontSize: "12px", color: "#64748b" },
  beneficiary: { fontFamily: "monospace", fontSize: "13px" },
  amount: { fontWeight: "700", color: "#011745" },
  statusBadge: { padding: "6px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" },
  dateCell: { fontSize: "13px", color: "#64748b" },
  viewBtn: { background: "#e0f2fe", color: "#0284c7", border: "none", borderRadius: "8px", padding: "8px 14px", fontSize: "13px", fontWeight: "600", cursor: "pointer" },
  emptyState: { textAlign: "center", padding: "60px 20px", color: "#64748b" },
  emptyIcon: { fontSize: "48px", marginBottom: "16px" },
  
  pagination: { display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", padding: "20px" },
  pageBtn: { background: "#f1f5f9", border: "none", borderRadius: "8px", padding: "10px 20px", fontSize: "14px", fontWeight: "600", cursor: "pointer", color: "#374151" },
  pageInfo: { color: "#64748b", fontSize: "14px" },
  
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" },
  modalContent: { background: "white", borderRadius: "20px", width: "100%", maxWidth: "600px", maxHeight: "90vh", overflow: "auto" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid #e2e8f0" },
  modalTitle: { margin: 0, fontSize: "20px", fontWeight: "700", color: "#011745" },
  modalClose: { background: "#f1f5f9", border: "none", borderRadius: "8px", width: "36px", height: "36px", fontSize: "18px", cursor: "pointer", color: "#64748b" },
  modalBody: { padding: "24px" },
  detailSection: { marginBottom: "24px" },
  detailHeader: { display: "flex", alignItems: "center", gap: "16px" },
  detailIcon: { fontSize: "40px" },
  detailType: { margin: "0 0 8px 0", fontSize: "18px", fontWeight: "700", color: "#011745" },
  detailAmount: { marginLeft: "auto", fontSize: "24px", fontWeight: "700", color: "#011745" },
  detailGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" },
  detailItem: { background: "#f8fafc", borderRadius: "12px", padding: "14px" },
  detailLabel: { display: "block", fontSize: "12px", color: "#64748b", marginBottom: "4px", fontWeight: "600" },
  detailValue: { display: "block", fontSize: "14px", color: "#011745", fontWeight: "500", wordBreak: "break-all" },
  modalFooter: { display: "flex", gap: "12px", padding: "20px 24px", borderTop: "1px solid #e2e8f0", justifyContent: "flex-end" },
  closeBtn: { background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: "10px", padding: "12px 20px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }
};
